import {
  IonBadge, IonButton, IonButtons, IonCard, IonCardContent,
  IonContent, IonFooter, IonHeader, IonIcon,
  IonInput,
  IonItem,
  IonLabel, IonModal, IonSegment, IonSegmentButton, IonSpinner, IonText, IonTitle, IonToast, IonToolbar
} from '@ionic/react';
import {
  arrowBack,
  businessOutline, chatbubbleEllipsesOutline, checkmarkCircleOutline,
  cloudDoneOutline, documentTextOutline, logoWhatsapp, phonePortraitOutline,
  refreshOutline, sendOutline, shieldCheckmarkOutline
} from 'ionicons/icons';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { isRetryableMetaCompletionError, metaSignupDeadline, metaSignupErrorMessage } from '../../../../../Utils/MetaEmbeddedSignup';
import { isAllowedMetaMessageOrigin, loadMetaFacebookSdk } from '../../../../../Utils/MetaFacebookSdk';
import {
  closeNativeMetaSignup, forgetNativeSignup, isNativeMetaSignup, openNativeMetaSignup,
  readPendingNativeSignup, rememberNativeSignup,
} from '../../../../../Utils/NativeMetaSignup';
import {
  requestService, whatsappMetaActivate, whatsappMetaDisable,
  whatsappMetaOnboardingComplete, whatsappMetaOnboardingResult, whatsappMetaOnboardingSession,
  whatsappMetaReviewAssets, whatsappMetaReviewMessage, whatsappMetaReviewTemplate,
  whatsappMetaStatus,
  whatsappMetaTest,
} from '../../../../../Utils/Services';
import './WhatsAppMetaCard.css';
import WhatsAppOfficialGuide, { WhatsAppOfficialRequirements, WhatsAppOfficialSteps } from './WhatsAppOfficialGuide';
import WhatsAppSendPreferences from './WhatsAppSendPreferences';

type Props = { empresaId: string; hideTrigger?: boolean; openRequest?: number; statusVisible?: boolean; onStatusChange?: (status: MetaStatus | null) => void };
type ReviewSection = 'messaging' | 'templates';
type ReviewTemplateSuggestion = {
  key: string;
  title: string;
  description: string;
  name: string;
  language: string;
  category: string;
  body: string;
  variables: Array<{ position: number; label: string; example: string }>;
};
export type MetaStatus = {
  available: boolean;
  status: string;
  activeChannel: 'baileys' | 'meta_cloud_api' | 'none';
  reviewDemoAvailable?: boolean;
  integration?: {
    status?: string;
    wabaId?: string;
    phoneNumberId?: string;
    displayPhoneNumber?: string;
    verifiedName?: string;
    platformType?: string;
    isOnBizApp?: boolean;
    lastError?: { code?: string; message?: string };
  };
};
type ReviewAssets = {
  graphApiVersion: string;
  permissions: string[];
  waba: { id: string; name?: string; accountReviewStatus?: string };
  phone: {
    id: string;
    displayPhoneNumber?: string;
    verifiedName?: string;
    status?: string;
    qualityRating?: string;
    platformType?: string;
  };
  templates: Array<{ id?: string; name: string; status?: string; category?: string; language?: string }>;
  templateSuggestions?: ReviewTemplateSuggestion[];
  checkedAt: string;
};
type ReviewResult = {
  ok: boolean;
  reused?: boolean;
  suggestionKey?: string;
  messageAccepted?: boolean;
  messageReference?: string;
  recipient?: string;
  template?: string | { id?: string; name: string; status?: string; category?: string; language?: string };
  sentAt?: string;
  createdAt?: string;
};
type SignupSession = {
  sessionId: string;
  state?: string;
  nonce?: string;
  expiresAt?: string;
  native?: { url: string };
  publicConfig: { appId: string; graphApiVersion: string; loginOptions: Record<string, any> };
};
type ActiveFlow = Omit<SignupSession, 'publicConfig'> & {
  publicConfig?: SignupSession['publicConfig'];
  empresaId: string;
  expiresAtMs: number;
  facebook?: NonNullable<Window['FB']>;
  loginStarted?: boolean;
  code?: string;
  embeddedSignup?: Record<string, any>;
  completing?: boolean;
  timeoutId?: number;
  resultTimeoutId?: number;
  checkingResult?: boolean;
};

function endpoint(template: string, empresaId: string) {
  return template.replace('{empresaId}', encodeURIComponent(empresaId));
}

function reviewRequestId(prefix: string) {
  return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 12)}`;
}

function suggestionExample(suggestion?: ReviewTemplateSuggestion) {
  if (!suggestion) return '';
  return suggestion.variables.reduce(
    (text, variable) => text.replace(`{{${variable.position}}}`, variable.example),
    suggestion.body,
  );
}

const WhatsAppMetaCard: React.FC<Props> = ({ empresaId, hideTrigger = false, openRequest = 0, statusVisible = true, onStatusChange }) => {
  const history = useHistory();
  const [status, setStatus] = useState<MetaStatus | null>(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [statusError, setStatusError] = useState(false);
  const [busy, setBusy] = useState(false);
  const [signupReady, setSignupReady] = useState(false);
  const [waitingForMeta, setWaitingForMeta] = useState(false);
  const [checkingNativeResult, setCheckingNativeResult] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  useEffect(() => { if (openRequest > 0) setShowModal(true); }, [openRequest]);
  useEffect(() => { onStatusChange?.(status); }, [onStatusChange, status]);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [toast, setToast] = useState('');
  const [canRetryCompletion, setCanRetryCompletion] = useState(false);
  const [canCheckNativeResult, setCanCheckNativeResult] = useState(false);
  const [reviewBusy, setReviewBusy] = useState(false);
  const [reviewAssets, setReviewAssets] = useState<ReviewAssets | null>(null);
  const [reviewSection, setReviewSection] = useState<ReviewSection>('messaging');
  const [selectedReviewTemplateKey, setSelectedReviewTemplateKey] = useState('');
  const [reviewRecipient, setReviewRecipient] = useState('');
  const [reviewMessageResult, setReviewMessageResult] = useState<ReviewResult | null>(null);
  const [reviewTemplateResult, setReviewTemplateResult] = useState<ReviewResult | null>(null);
  const mounted = useRef(true);
  const flow = useRef<ActiveFlow | null>(null);
  const flowGeneration = useRef(0);
  const activeEmpresaId = useRef(empresaId);
  activeEmpresaId.current = empresaId;

  const clearFlow = useCallback(() => {
    const current = flow.current;
    if (current?.timeoutId) window.clearTimeout(current.timeoutId);
    if (current?.resultTimeoutId) window.clearTimeout(current.resultTimeoutId);
    if (current?.native) {
      forgetNativeSignup(current.sessionId);
      void closeNativeMetaSignup(current.sessionId);
    }
    flow.current = null;
    flowGeneration.current += 1;
    if (mounted.current) {
      setCanRetryCompletion(false);
      setSignupReady(false);
      setWaitingForMeta(false);
      setCheckingNativeResult(false);
      setCanCheckNativeResult(false);
    }
  }, []);

  const api = useCallback(<T,>(url: string, method: string, body?: unknown) => new Promise<T>((resolve, reject) => {
    requestService(url, {
      method,
      credentials: 'include',
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    }, history, (value: any) => resolve(value as T), reject, true);
  }), [history]);

  const refresh = useCallback(async () => {
    setStatusError(false);
    if (!empresaId) {
      setStatus(null);
      setStatusLoading(false);
      return;
    }
    setStatusLoading(true);
    try {
      const next = await api<MetaStatus>(endpoint(whatsappMetaStatus.url, empresaId), whatsappMetaStatus.method);
      if (mounted.current && activeEmpresaId.current === empresaId) setStatus(next);
      return next;
    } catch (_) {
      if (mounted.current && activeEmpresaId.current === empresaId) {
        setStatus(null);
        setStatusError(true);
      }
    } finally {
      if (mounted.current && activeEmpresaId.current === empresaId) setStatusLoading(false);
    }
  }, [api, empresaId]);

  const completeIfReady = useCallback(async () => {
    const current = flow.current;
    if (!current || current.completing || !current.code || !current.embeddedSignup) return;
    if (Date.now() >= current.expiresAtMs) {
      clearFlow();
      setBusy(false);
      setToast('O tempo do cadastro expirou. Inicie novamente.');
      return;
    }
    if (current.empresaId !== empresaId) {
      clearFlow();
      setBusy(false);
      setToast('A empresa selecionada mudou. Inicie o cadastro novamente.');
      return;
    }
    current.completing = true;
    setWaitingForMeta(false);
    let completingGeneration = flowGeneration.current;
    setBusy(true);
    setCanRetryCompletion(false);
    try {
      await api(endpoint(whatsappMetaOnboardingComplete.url, empresaId), whatsappMetaOnboardingComplete.method, {
        sessionId: current.sessionId,
        state: current.state || current.nonce,
        code: current.code,
        embeddedSignup: current.embeddedSignup,
      });
      if (!mounted.current || flow.current !== current) return;
      clearFlow();
      completingGeneration = flowGeneration.current;
      const completedGeneration = flowGeneration.current;
      const next = await refresh();
      if (!mounted.current || flowGeneration.current !== completedGeneration) return;
      setShowModal(next?.status === 'configuration_pending');
      setToast(next?.status === 'connected' && next.activeChannel === 'meta_cloud_api'
        ? 'Autorização renovada. A integração continua ativa.'
        : 'Autorização concluída. Confira os dados da conexão.');
    } catch (error: any) {
      if (!mounted.current || flow.current !== current) return;
      current.completing = false;
      if (Date.now() < current.expiresAtMs && isRetryableMetaCompletionError(error)) {
        setCanRetryCompletion(true);
        setToast('Não foi possível concluir agora. Use “Tentar concluir novamente” para continuar com a mesma autorização.');
      } else {
        clearFlow();
        completingGeneration = flowGeneration.current;
        setToast(Date.now() >= current.expiresAtMs
          ? 'O tempo do cadastro expirou. Inicie novamente.'
          : error?.reauthorizationRequired === true
            ? 'A Meta precisa de uma nova autorização. Inicie o cadastro novamente.'
          : error?.retryable === false
            ? 'Não foi possível concluir a configuração. Entre em contato com o suporte para verificar esta tentativa.'
          : 'Não foi possível concluir a autorização. Verifique os dados e inicie o cadastro novamente.');
      }
    } finally {
      if (mounted.current && flowGeneration.current === completingGeneration) setBusy(false);
    }
  }, [api, clearFlow, empresaId, refresh]);

  const checkNativeResult = useCallback(async () => {
    const current = flow.current;
    if (!current?.native || current.checkingResult || current.empresaId !== activeEmpresaId.current) return;
    current.checkingResult = true;
    setCheckingNativeResult(true);
    setWaitingForMeta(false);
    setCanCheckNativeResult(false);
    setBusy(true);
    try {
      const url = endpoint(whatsappMetaOnboardingResult.url, current.empresaId)
        .replace('{sessionId}', encodeURIComponent(current.sessionId));
      const result = await api<{ status: string }>(url, whatsappMetaOnboardingResult.method);
      if (!mounted.current || flow.current !== current) return;
      if (result.status === 'completed') {
        clearFlow();
        const generation = flowGeneration.current;
        const next = await refresh();
        if (!mounted.current || generation !== flowGeneration.current) return;
        setShowModal(next?.status === 'configuration_pending');
        setToast(next?.status === 'connected' && next.activeChannel === 'meta_cloud_api'
          ? 'Autorização renovada. A integração continua ativa.'
          : 'Autorização concluída. Confira os dados da conexão.');
        setBusy(false);
      } else if (result.status === 'claimed' && Date.now() < current.expiresAtMs) {
        setWaitingForMeta(true);
        current.resultTimeoutId = window.setTimeout(() => void checkNativeResult(), 2000);
      } else {
        clearFlow();
        setBusy(false);
        setToast(result.status === 'expired' || Date.now() >= current.expiresAtMs
          ? 'O tempo do cadastro expirou. Inicie novamente.'
          : result.status === 'reauthorization_required'
            ? 'A Meta precisa de uma nova autorização. Inicie o cadastro novamente.'
            : result.status === 'retry_required'
              ? 'A conclusão foi interrompida. Confira a conexão e inicie uma nova autorização se necessário.'
            : 'A autorização não foi concluída. Você pode iniciar novamente.');
      }
    } catch (error: any) {
      if (!mounted.current || flow.current !== current) return;
      setCheckingNativeResult(false);
      if ([400, 401, 403, 404].includes(Number(error?.status || error?.statusCode))) {
        clearFlow();
        setBusy(false);
        setToast('Esta tentativa não está mais disponível. Confira a conexão antes de iniciar novamente.');
        void refresh();
        return;
      }
      setBusy(false);
      setCanCheckNativeResult(true);
      setToast('Não foi possível verificar a autorização. Toque em “Verificar autorização” para consultar novamente.');
    } finally { current.checkingResult = false; }
  }, [api, clearFlow, refresh]);

  useEffect(() => {
    mounted.current = true;
    setStatus(null);
    clearFlow();
    setBusy(false);
    setShowModal(false);
    setShowReviewModal(false);
    setShowGuide(false);
    setReviewAssets(null);
    setReviewSection('messaging');
    setSelectedReviewTemplateKey('');
    setReviewRecipient('');
    setReviewMessageResult(null);
    setReviewTemplateResult(null);
    const saved = isNativeMetaSignup() ? readPendingNativeSignup() : null;
    if (saved?.empresaId === empresaId) {
      flow.current = { ...saved, native: { url: '' }, expiresAtMs: Date.parse(saved.expiresAt), loginStarted: true };
      void checkNativeResult();
    }
    return () => { mounted.current = false; clearFlow(); };
  }, [checkNativeResult, clearFlow, empresaId, refresh]);

  useEffect(() => {
    if (statusVisible) void refresh();
  }, [refresh, statusVisible]);

  useEffect(() => {
    const listener = (message: MessageEvent) => {
      if (!isAllowedMetaMessageOrigin(message.origin) || !flow.current?.loginStarted || flow.current.native) return;
      let payload = message.data;
      if (typeof payload === 'string') {
        try { payload = JSON.parse(payload); } catch (_) { return; }
      }
      if (payload?.type !== 'WA_EMBEDDED_SIGNUP') return;
      const eventName = payload.event || payload.data?.event;
      if (eventName === 'CANCEL' || eventName === 'ERROR') {
        if (flow.current.completing || (flow.current.code && flow.current.embeddedSignup)) return;
        clearFlow();
        setBusy(false);
        setToast(eventName === 'CANCEL' ? 'Cadastro cancelado.' : metaSignupErrorMessage(payload));
        return;
      }
      if (eventName !== 'FINISH' && eventName !== 'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING') return;
      if (flow.current.completing || flow.current.embeddedSignup) return;
      const data = payload.data || {};
      if (!data.waba_id && !data.wabaId) return;
      flow.current.embeddedSignup = {
        ...data,
        wabaId: data.waba_id || data.wabaId,
        phoneNumberId: data.phone_number_id || data.phoneNumberId,
        businessId: data.business_id || data.businessId,
        onboardingType: 'whatsapp_business_app_onboarding',
        featureType: 'whatsapp_business_app_onboarding',
      };
      void completeIfReady();
    };
    window.addEventListener('message', listener);
    return () => window.removeEventListener('message', listener);
  }, [clearFlow, completeIfReady]);

  const startSignup = async () => {
    if (busy || flow.current) return;
    setBusy(true);
    const startingGeneration = flowGeneration.current;
    try {
      const native = isNativeMetaSignup();
      const session = await api<SignupSession>(endpoint(whatsappMetaOnboardingSession.url, empresaId), whatsappMetaOnboardingSession.method, native ? { native: true } : {});
      if (!mounted.current || flowGeneration.current !== startingGeneration || activeEmpresaId.current !== empresaId) return;
      const expiresAtMs = metaSignupDeadline(session.expiresAt);
      if (expiresAtMs <= Date.now()) {
        clearFlow();
        setBusy(false);
        setToast('O tempo do cadastro expirou. Inicie novamente.');
        return;
      }
      flow.current = {
        ...session,
        empresaId,
        expiresAtMs,
        timeoutId: window.setTimeout(() => {
          if (flow.current?.sessionId !== session.sessionId || flow.current.completing) return;
          clearFlow();
          setBusy(false);
          setToast('O tempo do cadastro expirou. Inicie novamente.');
        }, Math.max(0, expiresAtMs - Date.now())),
      };
      if (native) {
        if (!session.native?.url) throw new Error('Fluxo de autorização do aplicativo indisponível');
        const current = flow.current;
        current.loginStarted = true;
        rememberNativeSignup({ sessionId: session.sessionId, empresaId, expiresAt: new Date(expiresAtMs).toISOString() });
        setWaitingForMeta(true);
        const result = await openNativeMetaSignup(session.native.url, session.sessionId);
        if (!mounted.current || flow.current !== current || activeEmpresaId.current !== empresaId) return;
        if (result.sessionId !== session.sessionId) throw new Error('Retorno de outra autorização');
        // A deep link is only a wake-up signal. The authenticated API decides success.
        await checkNativeResult();
        return;
      }
      const facebook = await loadMetaFacebookSdk(session.publicConfig.appId, session.publicConfig.graphApiVersion);
      if (!mounted.current || flow.current?.sessionId !== session.sessionId) return;
      flow.current.facebook = facebook;
      setSignupReady(true);
      setBusy(false);
    } catch (error: any) {
      if (!mounted.current || flowGeneration.current !== startingGeneration) return;
      clearFlow();
      setBusy(false);
      setToast('Não foi possível preparar a conexão com a Meta. Tente novamente.');
    }
  };

  // O login precisa ocorrer diretamente no clique: aguardar rede/SDK aqui pode
  // expirar a ativação do usuário e bloquear o popup no Safari do celular.
  const openSignup = () => {
    const current = flow.current;
    if (busy || !current?.facebook || !current.publicConfig || current.loginStarted || current.empresaId !== empresaId) return;
    if (Date.now() >= current.expiresAtMs) {
      clearFlow();
      setToast('O tempo do cadastro expirou. Inicie novamente.');
      return;
    }
    current.loginStarted = true;
    setSignupReady(false);
    setWaitingForMeta(true);
    setBusy(true);
    try {
      current.facebook.login((result: any) => {
        if (!mounted.current || flow.current !== current || activeEmpresaId.current !== current.empresaId) return;
        if (current.completing || current.code) return;
        const code = result?.authResponse?.code;
        if (!code) {
          clearFlow();
          setBusy(false);
          setToast('Popup fechado ou autorização cancelada.');
          return;
        }
        current.code = code;
        void completeIfReady();
      }, current.publicConfig.loginOptions);
    } catch (_) {
      clearFlow();
      setBusy(false);
      setToast('Não foi possível abrir a janela da Meta. Permita pop-ups neste site e tente novamente.');
    }
  };

  const cancelSignup = () => {
    if (flow.current?.loginStarted) return;
    clearFlow();
    setBusy(false);
    setToast('Cadastro cancelado.');
  };

  const activate = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api(endpoint(whatsappMetaActivate.url, empresaId), whatsappMetaActivate.method, { configureOnly: true });
      await refresh();
      setShowModal(false);
      setToast('WhatsApp Oficial configurado. Escolha o canal de envio automático e salve para utilizá-lo.');
    } catch (error: any) {
      setToast(error?.message || 'Não foi possível ativar a integração.');
    } finally { setBusy(false); }
  };

  const testConnection = async () => {
    setBusy(true);
    try {
      await api(endpoint(whatsappMetaTest.url, empresaId), whatsappMetaTest.method, {});
      setToast('WABA, número e token validados com sucesso.');
    } catch (error: any) { setToast(error?.message || 'Falha ao validar a integração.'); }
    finally { setBusy(false); }
  };

  const disable = async () => {
    const accepted = window.confirm('Desconectar a integração oficial do Xeguei? Isso não exclui a conta nem remove o número do WhatsApp Business.');
    if (!accepted) return;
    setBusy(true);
    try {
      await api(endpoint(whatsappMetaDisable.url, empresaId), whatsappMetaDisable.method, {});
      await refresh();
      setShowModal(false);
      setToast('Integração oficial desconectada.');
    } catch (error: any) { setToast(error?.message || 'Não foi possível desconectar a integração.'); }
    finally { setBusy(false); }
  };

  const loadReviewAssets = async () => {
    setReviewBusy(true);
    try {
      const result = await api<ReviewAssets>(endpoint(whatsappMetaReviewAssets.url, empresaId), whatsappMetaReviewAssets.method);
      setReviewAssets(result);
      setSelectedReviewTemplateKey((current) => (
        result.templateSuggestions?.some((suggestion) => suggestion.key === current)
          ? current
          : result.templateSuggestions?.[0]?.key || ''
      ));
    } catch (error: any) {
      setToast(error?.message || 'Não foi possível consultar os ativos da Test WABA.');
    } finally { setReviewBusy(false); }
  };

  const openReviewDemo = () => {
    setReviewSection('messaging');
    setReviewMessageResult(null);
    setReviewTemplateResult(null);
    setShowReviewModal(true);
    void loadReviewAssets();
  };

  const sendReviewMessage = async () => {
    if (!reviewRecipient.trim()) {
      setToast('Informe o destinatário autorizado no API Setup.');
      return;
    }
    setReviewBusy(true);
    setReviewMessageResult(null);
    try {
      const result = await api<ReviewResult>(endpoint(whatsappMetaReviewMessage.url, empresaId), whatsappMetaReviewMessage.method, {
        requestId: reviewRequestId('message'),
        recipient: reviewRecipient,
      });
      setReviewMessageResult(result);
      setReviewRecipient('');
      setToast('Mensagem hello_world aceita pela Meta. Confirme o recebimento no celular.');
    } catch (error: any) {
      setToast(error?.message || 'A Meta não concluiu o envio de demonstração.');
    } finally { setReviewBusy(false); }
  };

  const createReviewTemplate = async () => {
    const suggestion = reviewAssets?.templateSuggestions?.find((item) => item.key === selectedReviewTemplateKey);
    if (!suggestion) {
      setToast('Escolha uma sugestão de template do Xeguei.');
      return;
    }
    const accepted = window.confirm(`Preparar o modelo “${suggestion.title}” exclusivamente na Test WABA? Se ele já existir, será reutilizado sem criar uma cópia.`);
    if (!accepted) return;
    setReviewBusy(true);
    setReviewTemplateResult(null);
    try {
      const result = await api<ReviewResult>(endpoint(whatsappMetaReviewTemplate.url, empresaId), whatsappMetaReviewTemplate.method, {
        requestId: reviewRequestId('template'),
        templateKey: suggestion.key,
      });
      setReviewTemplateResult(result);
      await loadReviewAssets();
      setToast(result.reused
        ? 'Template já existente reutilizado. Mostre o status retornado pela Meta.'
        : 'Template criado na Test WABA. Mostre o status retornado pela Meta.');
    } catch (error: any) {
      setToast(error?.message || 'A Meta não concluiu a criação do template.');
    } finally { setReviewBusy(false); }
  };

  if (!empresaId || (status && !status.available)) return null;
  const connected = status?.status === 'connected';
  const pending = status?.status === 'configuration_pending';
  const integration = status?.integration;
  const selectedReviewTemplate = reviewAssets?.templateSuggestions?.find(
    (suggestion) => suggestion.key === selectedReviewTemplateKey,
  );
  const progressLabel = checkingNativeResult ? 'Verificando conexão…'
    : waitingForMeta ? 'Aguardando autorização…'
      : flow.current?.completing ? 'Concluindo conexão…'
        : pending || connected ? 'Atualizando conexão…' : 'Preparando conexão…';
  const actionLabel = busy ? progressLabel
    : canCheckNativeResult ? 'Verificar autorização'
      : canRetryCompletion ? 'Tentar concluir novamente'
        : pending || connected ? 'Configurar oficial'
          : signupReady ? 'Continuar na Meta' : 'Conectar WhatsApp';

  const closeModal = () => {
    setShowModal(false);
    // Closing the instructions cancels only preparation, never an authorization
    // already opened with Meta or an activation request already in progress.
    if (!pending && !connected && !flow.current?.loginStarted) {
      clearFlow();
      setBusy(false);
    }
  };

  return <>
    {!hideTrigger && <IonButton size="large" disabled={statusLoading && !status} aria-busy={statusLoading && !status} onClick={() => setShowModal(true)}>
      {pending || connected ? 'Configurar oficial' : 'Ativar oficial'}
    </IonButton>}

    <IonModal className="wa-official-modal" isOpen={showModal} onDidDismiss={closeModal}>
      <IonHeader>
        <IonToolbar color="primary">
          <IonButtons slot="start"><IonButton aria-label="Voltar" onClick={closeModal}><IonIcon slot="icon-only" icon={arrowBack} /></IonButton></IonButtons>
          <IonTitle>WhatsApp Oficial</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding integracoes-modal-content wa-official-content">
        <IonCard className="integracoes-card"><IonCardContent>
          <div className="wa-help-heading">
            <IonIcon icon={logoWhatsapp} color="success" aria-hidden="true" />
            <div className="integracoes-section-title"><h2>{connected ? 'WhatsApp Oficial conectado' : pending ? 'Concluir ativação' : 'Ativar WhatsApp Oficial'}</h2><p>Use o mesmo número no Xeguei e no WhatsApp Business do celular com a coexistência.</p></div>
          </div>
          {/* <IonButton className="wa-official-guide-action" fill="clear" onClick={() => setShowGuide(true)}><IonIcon slot="start" icon={bookOutline} />Guia do WhatsApp Oficial</IonButton> */}
        </IonCardContent></IonCard>
        {statusLoading && !status ? <div className="whatsapp-meta-modal-error" role="status">
          <IonSpinner name="crescent" />
          <p>Consultando WhatsApp Oficial…</p>
        </div> : statusError ? <div className="whatsapp-meta-modal-error" role="alert">
          <IonText color="danger"><p>Não foi possível consultar o WhatsApp Oficial. Verifique sua conexão e tente novamente.</p></IonText>
          <IonButton disabled={statusLoading} onClick={() => void refresh()}>Tentar novamente</IonButton>
        </div> : !pending && !connected && <>
          <IonCard className="integracoes-card"><IonCardContent>
            <div className="integracoes-section-title"><h2>1. Antes de começar</h2></div>
            <WhatsAppOfficialRequirements />
          </IonCardContent></IonCard>
          <IonCard className="integracoes-card"><IonCardContent>
            <div className="integracoes-section-title"><h2>2. Conectar com a Meta</h2></div>
            <p>Ao continuar, uma janela da Meta será aberta. Siga as etapas e retorne ao Xeguei para concluir.</p>
            <WhatsAppOfficialSteps />
          <div className="whatsapp-meta-modal-actions">
            <IonButton className="whatsapp-meta-primary-action" expand="block" size="large" disabled={busy} aria-busy={busy} onClick={canCheckNativeResult ? () => void checkNativeResult() : canRetryCompletion ? () => void completeIfReady() : pending || connected ? () => setShowModal(true) : signupReady ? openSignup : startSignup}>
              <span className="whatsapp-meta-action-label" aria-live="polite" aria-atomic="true">
                {busy && <IonSpinner name="crescent" aria-hidden="true" />}{actionLabel}
              </span>
            </IonButton>
            {signupReady && <IonText className="whatsapp-meta-action-help" color="medium"><p>Tudo pronto. Toque em “Continuar na Meta” para autorizar a conexão do WhatsApp.</p></IonText>}
            {checkingNativeResult && <IonText className="whatsapp-meta-action-help" color="medium"><p>Aguarde enquanto confirmamos a conexão do WhatsApp.</p></IonText>}
            {waitingForMeta && !checkingNativeResult && <IonText className="whatsapp-meta-action-help" color="medium"><p>{flow.current?.native
              ? 'Conclua a autorização na janela aberta sobre o aplicativo. A conexão será verificada ao retornar.'
              : 'Conclua ou cancele a autorização na janela da Meta. Se ela não abriu, verifique se o navegador bloqueou pop-ups neste site.'}</p></IonText>}
            {signupReady && <IonButton fill="clear" onClick={cancelSignup}>Cancelar cadastro</IonButton>}
            {canRetryCompletion && <IonText className="whatsapp-meta-action-help" color="medium"><p>A autorização foi recebida. Tente concluir novamente antes que a sessão expire.</p></IonText>}

          </div>
          </IonCardContent></IonCard>
        </>}
        {(pending || connected) && <IonCard className="integracoes-card">
          <IonCardContent>
            <div className="integracoes-section-title"><h2>Dados da conexão</h2></div>
            <p><strong>Status da conexão:</strong> {connected ? 'Configurada' : 'Configuração pendente'}</p>
            <p><strong>Selecionada para envio:</strong> {status?.activeChannel === 'meta_cloud_api' ? 'Sim' : 'Não'}</p>
            <p><strong>WABA ID:</strong> {integration?.wabaId || 'Não informado'}</p>
            <p><strong>Phone Number ID:</strong> {integration?.phoneNumberId || 'Não informado'}</p>
            <p><strong>Número:</strong> {integration?.displayPhoneNumber || 'Não informado'}</p>
            <p><strong>Plataforma:</strong> {integration?.platformType || 'Não informada'}</p>
            <p><strong>WhatsApp Business App:</strong> {integration?.isOnBizApp === true ? 'Coexistência confirmada' : 'Aguardando confirmação'}</p>
            {integration?.lastError?.message && <IonText color="danger"><p>{integration.lastError.message}</p></IonText>}
          </IonCardContent>
        </IonCard>}
        {pending && <IonCard className="integracoes-card"><IonCardContent>
          <div className="integracoes-section-title"><h2>Última etapa</h2></div>
          <p>Conclua a configuração sem alterar o canal de envio atual. Depois selecione WhatsApp Oficial em Canal de envio automático e salve.</p>
          <IonButton expand="block" disabled={busy} onClick={activate}>Concluir configuração</IonButton>
        </IonCardContent></IonCard>}
        {connected && status?.activeChannel === 'meta_cloud_api' && showModal && <WhatsAppSendPreferences key={empresaId} empresaId={empresaId} />}
        {connected && <IonCard className="integracoes-card"><IonCardContent>
          <div className="integracoes-section-title"><h2>Integração configurada</h2></div>
          <p>{integration?.verifiedName} {integration?.displayPhoneNumber}</p>
          <IonButton expand="block" disabled={busy} onClick={testConnection}>Validar conexão</IonButton>
          <IonButton expand="block" color="danger" fill="outline" disabled={busy} onClick={disable}>Desconectar do Xeguei</IonButton>
          <IonText color="medium"><p>Desconectar não exclui a conta, não remove o número do WhatsApp Business e não troca o canal selecionado. Se o Oficial for o canal escolhido, seus envios ficarão indisponíveis até reconectar ou selecionar outro canal.</p></IonText>
        </IonCardContent></IonCard>}
        {status?.reviewDemoAvailable && <IonButton expand="block" fill="outline" color="tertiary" disabled={reviewBusy} onClick={() => {
          setShowModal(false);
          openReviewDemo();
        }}>
          Demonstração da App Review
        </IonButton>}
      </IonContent>
      {/* <IonFooter><IonToolbar><IonButton slot="end" fill="clear" onClick={closeModal}>Fechar</IonButton></IonToolbar></IonFooter> */}
    </IonModal>

    <WhatsAppOfficialGuide isOpen={showGuide} onClose={() => setShowGuide(false)} />

    <IonModal className="xeguei-review-modal" isOpen={showReviewModal} onDidDismiss={() => setShowReviewModal(false)}>
      <IonToolbar className="xeguei-review-toolbar">
        <IonTitle>Demonstração da App Review</IonTitle>
      </IonToolbar>
      <IonContent>
        <div className="xeguei-review-shell">
          <section className="xeguei-review-hero">
            <div>
              <span className="xeguei-review-eyebrow"><IonIcon icon={shieldCheckmarkOutline} /> AMBIENTE CONTROLADO</span>
              <h1>WhatsApp Oficial no Xeguei</h1>
              <p>Demonstre cada permissão separadamente usando apenas os ativos de teste fornecidos pela Meta.</p>
            </div>
            <div className="xeguei-review-hero-badges">
              <span>Graph API {reviewAssets?.graphApiVersion || 'v26.0'}</span>
              <span>Test WABA</span>
            </div>
          </section>

          {reviewBusy && !reviewAssets ? <div className="xeguei-review-loading">
            <IonSpinner name="crescent" />
            <p>Consultando ativos de teste na Meta…</p>
          </div> : reviewAssets ? <>
            <section className="xeguei-review-summary-grid" aria-label="Resumo dos ativos de teste">
              <div className="xeguei-review-summary-card">
                <IonIcon icon={businessOutline} />
                <div><span>Test WABA</span><strong>{reviewAssets.waba.name || 'Test WhatsApp Business Account'}</strong><small>{reviewAssets.waba.id}</small></div>
              </div>
              <div className="xeguei-review-summary-card">
                <IonIcon icon={phonePortraitOutline} />
                <div><span>Número de teste</span><strong>{reviewAssets.phone.displayPhoneNumber || 'Mascarado'}</strong><small>ID {reviewAssets.phone.id}</small></div>
              </div>
              <div className="xeguei-review-summary-card">
                <IonIcon icon={cloudDoneOutline} />
                <div><span>Conexão</span><strong>{reviewAssets.phone.status || 'Não informado'}</strong><small>{reviewAssets.phone.platformType || 'Cloud API'}</small></div>
              </div>
              <div className="xeguei-review-summary-card">
                <IonIcon icon={documentTextOutline} />
                <div><span>Modelos encontrados</span><strong>{reviewAssets.templates.length}</strong><small>Atualizados pela Graph API</small></div>
              </div>
            </section>

            <div className="xeguei-review-refresh-row">
              <span>Última consulta: {new Date(reviewAssets.checkedAt).toLocaleString('pt-BR')}</span>
              <IonButton size="small" fill="clear" disabled={reviewBusy} onClick={loadReviewAssets}>
                <IonIcon slot="start" icon={refreshOutline} /> Atualizar ativos
              </IonButton>
            </div>

            <IonSegment className="xeguei-review-tabs" value={reviewSection} onIonChange={(event) => setReviewSection(String(event.detail.value) as ReviewSection)}>
              <IonSegmentButton value="messaging">
                <IonIcon icon={chatbubbleEllipsesOutline} />
                <IonLabel>Envio de mensagem</IonLabel>
              </IonSegmentButton>
              <IonSegmentButton value="templates">
                <IonIcon icon={documentTextOutline} />
                <IonLabel>Modelos do Xeguei</IonLabel>
              </IonSegmentButton>
            </IonSegment>

            {reviewSection === 'messaging' ? <section className="xeguei-review-section">
              <header className="xeguei-review-section-header">
                <div className="xeguei-review-icon-tile messaging"><IonIcon icon={chatbubbleEllipsesOutline} /></div>
                <div>
                  <span className="xeguei-review-permission">whatsapp_business_messaging</span>
                  <h2>Enviar uma mensagem real de teste</h2>
                  <p>Use o modelo <code>hello_world</code> aprovado pela Meta e confirme o recebimento no celular autorizado.</p>
                </div>
              </header>

              <div className="xeguei-review-two-columns">
                <div className="xeguei-review-action-card">
                  <span className="xeguei-review-step">ETAPA 1</span>
                  <h3>Informe o destinatário autorizado</h3>
                  <p>Digite o número cadastrado no API Setup, com DDI e DDD.</p>
                  <IonItem className="xeguei-review-input" lines="none">
                    <IonIcon slot="start" icon={phonePortraitOutline} />
                    <IonInput
                      type="password"
                      inputMode="tel"
                      value={reviewRecipient}
                      placeholder="Ex.: 5511999999999"
                      onIonChange={(event) => setReviewRecipient(String(event.detail.value || ''))}
                    />
                  </IonItem>
                  <div className="xeguei-review-security-note">
                    <IonIcon icon={shieldCheckmarkOutline} />
                    <span>O número não é salvo e precisa constar na allowlist segura do backend.</span>
                  </div>
                  <IonButton className="xeguei-review-primary-action" expand="block" color="success" disabled={reviewBusy} onClick={sendReviewMessage}>
                    {reviewBusy ? <IonSpinner name="crescent" /> : <><IonIcon slot="start" icon={sendOutline} /> Enviar hello_world</>}
                  </IonButton>
                </div>

                <div className="xeguei-review-preview-card">
                  <span className="xeguei-review-step">PRÉVIA</span>
                  <h3>Mensagem enviada pela Meta</h3>
                  <div className="xeguei-review-phone-preview">
                    <div className="xeguei-review-phone-header"><IonIcon icon={logoWhatsapp} /> WhatsApp</div>
                    <div className="xeguei-review-chat-bubble">
                      <strong>Hello World</strong>
                      <p>Mensagem de teste oficial do WhatsApp Business Platform.</p>
                      <small>agora ✓✓</small>
                    </div>
                  </div>
                </div>
              </div>

              {reviewMessageResult?.messageAccepted && <div className="xeguei-review-result success">
                <IonIcon icon={checkmarkCircleOutline} />
                <div>
                  <h3>Mensagem aceita pela Meta</h3>
                  <p><strong>Modelo:</strong> {typeof reviewMessageResult.template === 'string' ? reviewMessageResult.template : reviewMessageResult.template?.name}</p>
                  <p><strong>Destinatário:</strong> {reviewMessageResult.recipient} · <strong>Referência:</strong> {reviewMessageResult.messageReference}</p>
                  <p>Mostre agora a mensagem recebida no celular durante a gravação.</p>
                </div>
              </div>}
            </section> : <section className="xeguei-review-section">
              <header className="xeguei-review-section-header">
                <div className="xeguei-review-icon-tile templates"><IonIcon icon={documentTextOutline} /></div>
                <div>
                  <span className="xeguei-review-permission">whatsapp_business_management</span>
                  <h2>Modelos sugeridos pelo Xeguei</h2>
                  <p>Escolha um modelo Utility padronizado. O backend aceita somente estas sugestões e reutiliza modelos existentes.</p>
                </div>
              </header>

              <div className="xeguei-review-template-layout">
                <div>
                  <span className="xeguei-review-step">1 · ESCOLHA UMA SUGESTÃO</span>
                  <div className="xeguei-review-suggestion-grid">
                    {!reviewAssets.templateSuggestions?.length && <p className="xeguei-review-empty-catalog">O catálogo de modelos ainda não foi carregado. Atualize os ativos após publicar o backend.</p>}
                    {(reviewAssets.templateSuggestions || []).map((suggestion) => {
                      const selected = suggestion.key === selectedReviewTemplateKey;
                      return <button
                        type="button"
                        key={suggestion.key}
                        aria-pressed={selected}
                        className={`xeguei-review-suggestion${selected ? ' selected' : ''}`}
                        onClick={() => setSelectedReviewTemplateKey(suggestion.key)}
                      >
                        <div className="xeguei-review-suggestion-heading">
                          <span>{suggestion.title}</span>
                          {selected && <IonIcon icon={checkmarkCircleOutline} />}
                        </div>
                        <p>{suggestion.description}</p>
                        <code>{suggestion.name}</code>
                      </button>;
                    })}
                  </div>
                </div>

                <aside className="xeguei-review-template-preview">
                  <span className="xeguei-review-step">2 · CONFIRA A PRÉVIA</span>
                  {selectedReviewTemplate ? <>
                    <div className="xeguei-review-template-meta">
                      <IonBadge color="primary">{selectedReviewTemplate.category}</IonBadge>
                      <span>{selectedReviewTemplate.language}</span>
                    </div>
                    <h3>{selectedReviewTemplate.title}</h3>
                    <code className="xeguei-review-template-name">{selectedReviewTemplate.name}</code>
                    <div className="xeguei-review-chat-bubble template">{suggestionExample(selectedReviewTemplate)}</div>
                    <div className="xeguei-review-variable-list">
                      {selectedReviewTemplate.variables.map((variable) => <span key={variable.position}>
                        {`{{${variable.position}}}`} {variable.label}: <strong>{variable.example}</strong>
                      </span>)}
                    </div>
                    <IonButton className="xeguei-review-primary-action" expand="block" color="tertiary" disabled={reviewBusy} onClick={createReviewTemplate}>
                      {reviewBusy ? <IonSpinner name="crescent" /> : <><IonIcon slot="start" icon={documentTextOutline} /> Criar ou reutilizar modelo</>}
                    </IonButton>
                    <p className="xeguei-review-safe-copy"><IonIcon icon={shieldCheckmarkOutline} /> Somente a Test WABA será alterada. Nenhum número será criado ou migrado.</p>
                  </> : <p>Selecione uma sugestão para visualizar o modelo.</p>}
                </aside>
              </div>

              {reviewTemplateResult && typeof reviewTemplateResult.template === 'object' && <div className="xeguei-review-result success">
                <IonIcon icon={checkmarkCircleOutline} />
                <div>
                  <h3>{reviewTemplateResult.reused ? 'Modelo existente reutilizado' : 'Modelo enviado para a Meta'}</h3>
                  <p><strong>Nome:</strong> {reviewTemplateResult.template.name}</p>
                  <p><strong>ID:</strong> {reviewTemplateResult.template.id || 'Aguardando retorno'} · <strong>Status:</strong> {reviewTemplateResult.template.status || 'PENDING'} · <strong>Categoria:</strong> {reviewTemplateResult.template.category}</p>
                </div>
              </div>}

              <details className="xeguei-review-existing-templates">
                <summary>Modelos já encontrados na Test WABA ({reviewAssets.templates.length})</summary>
                <div className="xeguei-review-template-list">
                  {reviewAssets.templates.length ? reviewAssets.templates.slice(0, 20).map((template) => <div key={template.id || template.name}>
                    <code>{template.name}</code>
                    <span>{template.status || 'SEM STATUS'}</span>
                    <small>{template.category || 'sem categoria'} · {template.language || 'sem idioma'}</small>
                  </div>) : <p>Nenhum modelo encontrado.</p>}
                </div>
              </details>
            </section>}
          </> : <div className="xeguei-review-loading">
            <p>Não foi possível carregar os ativos de teste.</p>
            <IonButton fill="outline" onClick={loadReviewAssets}>Tentar novamente</IonButton>
          </div>}
        </div>
      </IonContent>
      <IonFooter className="xeguei-review-footer"><IonToolbar><IonButton slot="end" fill="clear" onClick={() => setShowReviewModal(false)}>Fechar demonstração</IonButton></IonToolbar></IonFooter>
    </IonModal>
    <IonToast isOpen={Boolean(toast)} message={toast} duration={5000} onDidDismiss={() => setToast('')} />
  </>;
};

export default WhatsAppMetaCard;
